/**
 * Normalisation des énoncés Markdown+LaTeX avant KaTeX — le moteur web.
 *
 * Le mobile rend les énoncés avec Mathpix/MathJax (MarkdownWithLatex.js), le
 * web avec react-markdown + remark-math + KaTeX. Le second est moins tolérant :
 * un énoncé que le mobile affiche bien sort en LaTeX brut ou en rouge sur le
 * web. Le contenu stocké est bon ; ce module rapproche le web de la lecture
 * qu'en fait le mobile, qui reste la référence et n'est pas touché.
 *
 * ⚠️ Un seul module pour deux applis : le rendu web de l'app — `<RichText>`
 * (RichText.web.tsx) et le legacy MarkdownWithLatex.web.js, qui le lit par le
 * ré-export `common/utils/enonceLatex` — et le BO (packages/bo-web,
 * MarkdownLatex.tsx, via l'alias `@enonce-latex`). Il ne doit donc rien
 * importer : le BO le compile avec ses propres dépendances, sans celles du
 * frontend. Le BO le vérifie aussi avec SON tsc strict (`yarn typecheck` dans
 * packages/bo-web après toute modification), et ne l'embarque qu'à son
 * prochain build.
 *
 * Deux étages, parce que deux familles de défauts :
 * - `normalizeEnonceLatex` réécrit la source AVANT le parseur markdown, pour
 *   les délimiteurs de formule qu'il lit mal ;
 * - `remarkEnonceLatex` corrige l'arbre APRÈS le parseur, là où l'on sait ce
 *   qui est formule, texte ou code : on ne répare que le corps des formules,
 *   et les commandes de mise en forme — comme les retours à la ligne —
 *   seulement dans le texte.
 */

type MdastNode = {
    type: string
    value?: string
    children?: MdastNode[]
    position?: { start?: { offset?: number } }
    data?: Record<string, unknown>
}

// ─── Étage 1 : la source ────────────────────────────────────────────────────

const BACKSLASH_DELIMITERS: Array<[open: string, close: string, dollars: string]> = [
    // Forme doublée d'abord (`\\(` = backslash échappé + parenthèse en
    // markdown) : le mobile la lit comme une formule, comme la forme simple.
    ["\\\\(", "\\\\)", "$"],
    ["\\\\[", "\\\\]", "$$"],
    ["\\(", "\\)", "$"],
    ["\\[", "\\]", "$$"],
]

const BLANK_LINE = /\n[ \t]*\n/
const BLANK_LINE_HERE = new RegExp(BLANK_LINE.source, "y")

type Rewrite = { text: string; end: number }

/**
 * Réécrit, hors code, les délimiteurs de formule que le parseur markdown lit
 * mal :
 * - `\(…\)` / `\[…\]` (et la forme doublée `\\(…\\)`) deviennent `$…$` /
 *   `$$…$$` : `\(` est un échappement markdown, la formule sortait en texte ;
 * - un bloc `$$` sur plusieurs lignes dont l'ouverture ou la fermeture partage
 *   sa ligne avec du contenu (`$$\begin{array}…`, `…\end{array}$$Suite`) est
 *   remis avec `$$` seul sur sa ligne : sinon le parseur jette la première
 *   ligne de la formule, ou ne referme jamais le bloc et avale tout l'énoncé
 *   qui suit.
 */
export function normalizeEnonceLatex(markdown: string): string {
    return rewriteFormulas(markdown, (span) =>
        span.backslash
            ? {
                  text: `${span.dollars}${markdown.slice(span.from, span.to).trim()}${span.dollars}`,
                  end: span.end,
              }
            : canonicalDisplayBlock(markdown, span.start, span.end)
    )
}

/**
 * `keepFormulaSpaces` appliqué au corps de chaque formule d'un markdown, délimiteurs et code
 * intacts. Pour le moteur natif (Mathpix), qui reçoit la source : le web répare l'arbre
 * (`repairFormula`).
 */
export function keepSpacesInFormulas(markdown: string): string {
    if (!/[$\\]/.test(markdown ?? "")) return markdown
    return rewriteFormulas(
        markdown,
        ({ start, from, to, end }) => ({
            text: markdown.slice(start, from) + keepFormulaSpaces(markdown.slice(from, to)) + markdown.slice(to, end),
            end,
        }),
        { mathpixDollars: true }
    )
}

/**
 * Une formule de la source : `[start, end[` avec ses délimiteurs, `[from, to[`
 * son corps. `dollars` est son délimiteur en dollars (celui qui remplacerait
 * un `\(…\)`).
 */
type FormulaSpan = { start: number; from: number; to: number; end: number; dollars: string; backslash: boolean }

/**
 * Parcourt, hors code, chaque formule de `markdown` (`$…$`, `$$…$$`, `\(…\)`,
 * `\[…\]` et leur forme doublée) et remplace `[start, rewrite.end[` par
 * `rewrite.text` quand `rewrite` en rend un. `mathpixDollars` : un `$` simple
 * contre une espace (`$ … $`) n'ouvre pas de formule, comme chez Mathpix — c'est
 * du texte, souvent une somme en dollars, et le `$` suivant peut encore ouvrir
 * la vraie formule.
 */
function rewriteFormulas(
    markdown: string,
    rewrite: (span: FormulaSpan) => Rewrite | null,
    { mathpixDollars = false } = {}
): string {
    if (!markdown) return markdown

    let out = ""
    let copiedUpTo = 0
    let i = 0
    while (i < markdown.length) {
        const skipEnd = skipCodeOrDollarMath(markdown, i)
        let span: FormulaSpan | null = null
        if (skipEnd > i && markdown[i] === "$") {
            const run = runOf(markdown, i, "$")
            const [from, to] = [i + run, skipEnd - run]
            if (mathpixDollars && run === 1 && to > from && /\s/.test(markdown[from] + markdown[to - 1])) {
                i += run
                continue
            }
            // Un `$` jamais refermé n'a pas de corps.
            if (to >= from) span = { start: i, from, to, end: skipEnd, dollars: "$".repeat(run), backslash: false }
        } else if (skipEnd === i && markdown[i] === "\\") {
            const formula = findBackslashFormula(markdown, i)
            if (formula) span = { start: i, ...formula, backslash: true }
        }

        const rewritten = span && rewrite(span)
        if (rewritten) {
            out += markdown.slice(copiedUpTo, i) + rewritten.text
            i = copiedUpTo = rewritten.end
        } else if (skipEnd > i) {
            i = skipEnd
        } else {
            // Un `\x` ordinaire (`\$`, `\*`…) se saute d'un bloc, pour que le
            // caractère échappé ne soit pas relu comme délimiteur.
            i += markdown[i] === "\\" ? 2 : 1
        }
    }
    return copiedUpTo === 0 ? markdown : out + markdown.slice(copiedUpTo)
}

/** La formule `\(…\)` / `\[…\]` (forme doublée comprise) qui s'ouvre en `start`, ou null. */
function findBackslashFormula(
    source: string,
    start: number
): Pick<FormulaSpan, "from" | "to" | "end" | "dollars"> | null {
    for (const [open, close, dollars] of BACKSLASH_DELIMITERS) {
        if (!source.startsWith(open, start)) continue

        const from = start + open.length
        let to = source.indexOf(close, from)
        // Forme simple : `\\)` n'est pas une fermeture (saut de ligne LaTeX
        // suivi d'une parenthèse, ou fermeture de la forme doublée).
        while (open.length === 2 && to !== -1 && source[to - 1] === "\\") {
            to = source.indexOf(close, to + 1)
        }
        if (to === -1) return null

        const content = source.slice(from, to)
        if (!content.trim() || content.includes("$") || BLANK_LINE.test(content)) return null
        return { from, to, end: to + close.length, dollars }
    }
    return null
}

/**
 * La formule `[start, end[` est-elle un bloc `$$` en début de ligne, sur
 * plusieurs lignes, qui partage une ligne avec du contenu ? Si oui, la renvoie
 * en forme canonique (et `end` avance au-delà des blancs qui la suivent).
 */
function canonicalDisplayBlock(source: string, start: number, end: number): Rewrite | null {
    if (runOf(source, start, "$") !== 2) return null
    const indent = linePrefix(source, start)
    const body = source.slice(start + 2, end - 2)
    if (/\S/.test(indent) || !body.includes("\n")) return null

    const lineEnd = source.indexOf("\n", end)
    const after = source.slice(end, lineEnd === -1 ? source.length : lineEnd)
    const openingShared = /\S/.test(body.slice(0, body.indexOf("\n")))
    const closingShared = /\S/.test(body.slice(body.lastIndexOf("\n") + 1))
    const followed = /\S/.test(after)
    if (!openingShared && !closingShared && !followed) return null

    return {
        text: `$$\n${indent}${body.trim()}\n${indent}$$${followed ? `\n${indent}` : ""}`,
        end: followed ? end + after.length - after.trimStart().length : end,
    }
}

/**
 * Si `i` ouvre un bloc de code, un code en ligne ou une formule en dollars,
 * renvoie la fin de ce passage (à recopier tel quel) ; sinon renvoie `i`.
 */
function skipCodeOrDollarMath(source: string, i: number): number {
    const char = source[i]
    if (char !== "`" && char !== "~" && char !== "$") return i

    const run = runOf(source, i, char)
    const atLineStart = !/\S/.test(linePrefix(source, i))
    if (char !== "$" && run >= 3 && atLineStart) {
        const closing = new RegExp(`\\n[ \\t]*\\${char}{${run},}[ \\t]*(?=\\n|$)`, "g")
        closing.lastIndex = i + run
        const match = closing.exec(source)
        return match ? match.index + match[0].length : source.length
    }
    if (char === "~") return i

    // Code en ligne ou formule : jusqu'à la suite de même longueur. Un bloc
    // (`$$` en début de ligne) peut contenir des lignes vides ; le reste
    // s'arrête au paragraphe.
    const end = findRun(source, i + run, char, run, char === "$" && run >= 2 && atLineStart)
    return end === -1 ? i + run : end + run
}

/** Ce qui précède `i` sur sa ligne. */
function linePrefix(source: string, i: number): string {
    return source.slice(source.lastIndexOf("\n", i - 1) + 1, i)
}

function runOf(source: string, i: number, char: string): number {
    let n = 0
    while (source[i + n] === char) n += 1
    return n
}

/** Première suite d'exactement `length` `char` non échappée, à partir de `from`. */
function findRun(source: string, from: number, char: string, length: number, acrossParagraphs: boolean): number {
    let i = from
    while (i < source.length) {
        if (!acrossParagraphs && source[i] === "\n") {
            BLANK_LINE_HERE.lastIndex = i
            if (BLANK_LINE_HERE.test(source)) return -1
        }
        if (source[i] === "\\" && char !== "`") {
            i += 2
            continue
        }
        if (source[i] === char) {
            const run = runOf(source, i, char)
            if (run === length) return i
            i += run
            continue
        }
        i += 1
    }
    return -1
}

// ─── Étage 2 : l'arbre ──────────────────────────────────────────────────────

/**
 * Plugin remark, à placer après remark-math et à nourrir de la sortie de
 * `normalizeEnonceLatex` (il relit la source pour distinguer `$$` de `$`) :
 * - un `$$…$$` écrit dans une ligne s'affiche en bloc, comme sur mobile —
 *   et des blocs collés `$$a$$$$b$$` redeviennent des blocs distincts ;
 * - le corps des formules est réparé (voir `repairFormula`) ;
 * - `\textit{…}`, `\textbf{…}`, `\underline{…}`, `\text{…}` écrits dans le
 *   texte deviennent de l'italique, du gras, du souligné, du texte ;
 * - un simple retour à la ligne va à la ligne (voir `pushText`).
 */
export function remarkEnonceLatex() {
    return (tree: MdastNode, file?: { value?: unknown }) => {
        const source = typeof file?.value === "string" ? file.value : ""
        transformChildren(tree, source)
    }
}

function transformChildren(parent: MdastNode, source: string): void {
    if (!parent.children) return
    const children: MdastNode[] = []
    for (const child of parent.children) {
        if (child.type === "math") {
            children.push(repairBlockMath(child))
        } else if (child.type === "inlineMath") {
            children.push(...repairInlineMath(child, source))
        } else if (child.type === "text") {
            children.push(...convertTextCommands(child.value ?? ""))
        } else {
            transformChildren(child, source)
            children.push(child)
        }
    }
    parent.children = children
}

// mdast-util-math pose sur chaque formule, en plus de `value`, le nœud HTML
// que remark-rehype produira (`data.hChildren`) : c'est lui que rehype-katex
// lit. On le reconstruit donc à l'identique autour de la valeur réparée.

function repairBlockMath(node: MdastNode): MdastNode {
    const value = repairFormula(node.value ?? "")
    return {
        ...node,
        value,
        data: {
            hName: "pre",
            hChildren: [
                {
                    type: "element",
                    tagName: "code",
                    properties: { className: ["language-math", "math-display"] },
                    children: [{ type: "text", value }],
                },
            ],
        },
    }
}

function repairInlineMath(node: MdastNode, source: string): MdastNode[] {
    const offset = node.position?.start?.offset
    const display = offset !== undefined && source.startsWith("$$", offset)
    const tex = node.value ?? ""
    // `$$a$$$$b$$` : la suite `$$$$` ne ferme pas la formule ouverte par
    // `$$`, le parseur n'en voit qu'une, qui contient les autres.
    return (display ? tex.split("$$$$") : [tex]).map((body) => {
        const value = repairFormula(body)
        return {
            ...node,
            value,
            data: {
                hName: "code",
                hProperties: {
                    className: ["language-math", display ? "math-display" : "math-inline"],
                },
                hChildren: [{ type: "text", value }],
            },
        }
    })
}

/** Répare le corps d'une formule là où KaTeX refuse ce que MathJax accepte. */
export function repairFormula(tex: string): string {
    return keepFormulaSpaces(
        fillDanglingScripts(escapeUnderscoreRuns(escapeTextUnderscores(stripArrayColumnSeparators(tex))))
    )
}

const WORD_EDGE = /[A-Za-zÀ-ÖØ-öø-ÿ0-9)\]}]/

/**
 * En mode math, les deux moteurs ignorent les espaces de la source, et les
 * énoncés générés en écrivent deux que l'élève doit voir :
 * - entre les groupes de trois chiffres d'un nombre (`3 502 080 400`), rendue
 *   insécable (`~`) pour que le nombre ne se coupe pas en fin de ligne ;
 * - de part et d'autre d'un mot `\text{…}` (`20 \text{centaines}`), rendue
 *   `\ ` ; devant un opérateur, l'espace de l'opérateur suffit.
 * Un `\ ` déjà écrit et l'intérieur d'un `\text{…}`, où l'espace compte déjà,
 * ne bougent pas.
 */
export function keepFormulaSpaces(tex: string): string {
    if (!/[ \t]/.test(tex)) return tex
    const groups = [...bracedGroups(tex, TEXT_GROUP)]
    // Un mot qui porte déjà son espace (`\text{si } x`) n'en reçoit pas une seconde.
    const opensWord = new Set(groups.filter(({ open }) => /\S/.test(tex[open + 1])).map(({ start }) => start))
    const closesWord = new Set(groups.filter(({ close }) => /\S/.test(tex[close - 1])).map(({ close }) => close + 1))
    const inWord = (at: number) => groups.some(({ open, close }) => at > open && at < close)
    const digitGroup = /\d{3}(?!\d)/y

    // Un `\ ` déjà écrit est un échappement : `replaceUnescaped` le laisse.
    return replaceUnescaped(tex, "[ \\t]+", (run, at) => {
        const end = at + run.length
        const before = tex[at - 1] ?? ""
        if (!before || end === tex.length || inWord(at)) return run
        digitGroup.lastIndex = end
        // `10^3 000`, `x^12 345` : les chiffres d'avant sont un exposant ou un indice, pas un groupe.
        let digitsFrom = at
        while (/\d/.test(tex[digitsFrom - 1] ?? "")) digitsFrom -= 1
        const scripted = /[\^_]/.test(tex[digitsFrom - 1] ?? "")
        if (digitsFrom < at && !scripted && digitGroup.test(tex)) return "~"
        const wordAfter = opensWord.has(end) && WORD_EDGE.test(before)
        const wordBefore = closesWord.has(at) && WORD_EDGE.test(tex[end])
        return wordAfter || wordBefore ? "\\ " : run
    })
}

/** `\begin{array}{r@{\quad}l}` : KaTeX ne connaît pas `@{…}` ni `!{…}`. */
function stripArrayColumnSeparators(tex: string): string {
    let out = ""
    let from = 0
    for (const { open, close } of bracedGroups(tex, /\\begin\{array\}\{/g)) {
        const spec = tex.slice(open + 1, close)
        let kept = ""
        let specFrom = 0
        for (const separator of bracedGroups(spec, /[@!]\{/g)) {
            kept += spec.slice(specFrom, separator.start)
            specFrom = separator.close + 1
        }
        out += tex.slice(from, open + 1) + kept + spec.slice(specFrom)
        from = close
    }
    return out + tex.slice(from)
}

const TEXT_GROUP = /\\(?:text|textrm|textit|textbf|textnormal|textsf|texttt|textup)\{/g

/** `\text{___ Mann}` : en mode texte, KaTeX refuse `_` non échappé. */
function escapeTextUnderscores(tex: string): string {
    let out = ""
    let from = 0
    for (const { open, close } of bracedGroups(tex, TEXT_GROUP)) {
        const content = tex.slice(open + 1, close)
        // Une formule imbriquée (`\text{si $x_1$}`) garde ses indices.
        const escaped = content.includes("$") ? content : replaceUnescaped(content, "_", () => "\\_")
        out += tex.slice(from, open + 1) + escaped
        from = close
    }
    return out + tex.slice(from)
}

/** `2 \times ___ + 5` : une suite de `_` est un blanc à compléter, pas un indice. */
function escapeUnderscoreRuns(tex: string): string {
    return replaceUnescaped(tex, "_{2,}", (run) => "\\_".repeat(run.length))
}

/**
 * `68^` : KaTeX exige un argument après `^` ou `_`, MathJax pose un argument
 * vide. Après un nombre, on restitue plutôt le degré perdu (le `\circ` de
 * `68^\circ`) : dans les énoncés, un exposant vide après un nombre est
 * toujours un angle ou une température — le mobile, lui, affiche « 68 ».
 */
function fillDanglingScripts(tex: string): string {
    return replaceUnescaped(tex, /(?:\^+|_)\s*(?=$|\}|&|\\\\|\\end\b)/.source, (script, offset) =>
        script[0] === "^" && /\d/.test(tex[offset - 1] ?? "") ? "^{\\circ}" : `${script[0]}{}`
    )
}

/**
 * Remplace `pattern` partout sauf dans un `\x` (commande ou caractère
 * échappé) : `\_` et `\^` restent intacts. Sans lookbehind, que les Safari
 * antérieurs à 16.4 ne savent pas lire.
 */
function replaceUnescaped(tex: string, pattern: string, replace: (match: string, offset: number) => string): string {
    return tex.replace(new RegExp(`\\\\[\\s\\S]|${pattern}`, "g"), (token: string, offset: number) =>
        token[0] === "\\" ? token : replace(token, offset)
    )
}

/**
 * Les groupes `\cmd{…}` de `tex` qui ouvrent sur `pattern` (terminé par `{`),
 * accolades équilibrées et sans chevauchement ; un groupe jamais refermé est
 * ignoré.
 */
function* bracedGroups(tex: string, pattern: RegExp) {
    let from = 0
    for (const match of tex.matchAll(pattern)) {
        const start = match.index ?? 0
        if (start < from) continue
        const open = start + match[0].length - 1
        const close = matchingBrace(tex, open)
        if (close === -1) continue
        yield { start, open, close, name: match[1] }
        from = close + 1
    }
}

/** Index de l'accolade fermante qui répond à celle en `open`, ou -1. */
function matchingBrace(tex: string, open: number): number {
    let depth = 0
    for (let i = open; i < tex.length; i += 1) {
        if (tex[i] === "\\") {
            i += 1
        } else if (tex[i] === "{") {
            depth += 1
        } else if (tex[i] === "}") {
            depth -= 1
            if (depth === 0) return i
        }
    }
    return -1
}

// Commandes de mise en forme écrites hors formule. Le mobile les rend
// (italique, gras, souligné, texte) ; le web les affichait en brut.
const plain = (children: MdastNode[]): MdastNode[] => children
const emphasis = (children: MdastNode[]): MdastNode[] => [{ type: "emphasis", children }]
const TEXT_COMMAND_NODES: Record<string, (children: MdastNode[]) => MdastNode[]> = {
    text: plain,
    textrm: plain,
    textnormal: plain,
    textit: emphasis,
    emph: emphasis,
    textbf: (children) => [{ type: "strong", children }],
    // `<ins>` : le seul élément souligné que le nettoyage HTML du web laisse
    // passer. Les exercices « indique pour chaque mot souligné » en dépendent.
    underline: (children) => [{ type: "emphasis", data: { hName: "ins" }, children }],
}
const TEXT_COMMAND = new RegExp(`\\\\(${Object.keys(TEXT_COMMAND_NODES).join("|")})\\{`, "g")

function convertTextCommands(value: string): MdastNode[] {
    const nodes: MdastNode[] = []
    let from = 0
    for (const { start, open, close, name } of bracedGroups(value, TEXT_COMMAND)) {
        pushText(nodes, value.slice(from, start))
        const inner = convertTextCommands(value.slice(open + 1, close))
        for (const node of TEXT_COMMAND_NODES[name](inner)) {
            if (node.type === "text") pushText(nodes, node.value ?? "")
            else nodes.push(node)
        }
        from = close + 1
    }
    pushText(nodes, value.slice(from))
    return nodes
}

// Un simple retour à la ligne ne vaut qu'une espace en markdown, alors que le
// mobile le rend en retour à la ligne : les énoncés écrivent leurs sous-
// questions ligne à ligne (« a) … » puis « b) … »), et sur le web l'élève les
// lisait d'un seul tenant. C'est ce que fait `remark-breaks`, inliné ici pour
// que le module reste sans dépendance. Tout le texte que ce module émet passe
// par `pushText` — et seul le texte : le contenu d'une formule ou d'un bloc de
// code n'est pas un nœud texte.
const LINE_BREAK = /[\t ]*(?:\r?\n|\r)[\t ]*/

function pushText(nodes: MdastNode[], value: string): void {
    value.split(LINE_BREAK).forEach((line, index) => {
        if (index > 0) nodes.push({ type: "break" })
        if (!line) return
        const last = nodes[nodes.length - 1]
        if (last?.type === "text") last.value = (last.value ?? "") + line
        else nodes.push({ type: "text", value: line })
    })
}
